import {useContext, useState} from "react";
import {useMessages} from "../../hooks/useMessages";
import {getLocalStorageItem, removeLocalStorageItem} from "../../shared/localStorageUtil";
import {AddItemType} from "../../types/AddItemType";
import {ILoggedOutShoppingList} from "../../interfaces/ILoggedOutShoppingList";
import {signIn} from "next-auth/react";
import Form from "react-bootstrap/Form";
import Button from "../Button";
import Link from "next/link";
import styled from "styled-components";
import CustomInput, {InputTypeEnum} from "../CustomInput";
import {LoggedOutCartContext} from "../../contexts/LoggedOutCartContext";
import {LoggedOutWishlistContext} from "../../contexts/LoggedOutWishlistContext";
import {getNavPath} from "../../shared/navigateUtil";
import {useRouter} from "next/router";
import {Spinner} from "react-bootstrap";
import {getTheme} from "../../shared/themeUtils";

const LoginContain = styled.div`
  width: 100%;
  .error-section {
    margin-top: ${getTheme("space.3")};
    color: ${getTheme("colors.textRed")};
    font-weight: bold;
  }

  .login-form {
    margin-top: ${getTheme("space.3")};
    text-align: left;


    .form-check {
      
      label {
        font-size: ${getTheme("fontSizes.small")};
        color: ${getTheme("colors.secondaryFont")};
      }

      input.form-control {
        outline: none;
        border-color: ${getTheme("colors.primary")};
      }

      input.form-control:focus {
        border-color: ${getTheme("colors.primary")};
      }

      .form-check-input {
        width: 1.2em;
        height: 1.2em;
        border-radius: ${getTheme("borderRadius")};
        position: relative;
        bottom: 1px;
        margin-right: ${getTheme("space.1")};
      }

      .form-check-input:checked {
        background-color: ${getTheme("colors.primary")};
        border-color: ${getTheme("colors.primary")};
      }
    }
  }

  .forgot-password {
    margin-top: ${getTheme("space.2")};
    span {
      cursor: pointer;
  }
}
`

const Login = () => {
  const router = useRouter();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("")
  const messagesQuery = useMessages()
  const {setLoggedOutCartCtx} = useContext(LoggedOutCartContext)
  const {setLoggedOutWishlistCtx} = useContext(LoggedOutWishlistContext)
  const [isLoading, setIsLoading] = useState(false)
  const {redirectCallback} = router.query

  const handleLoginSubmit = async (e: any) => {
    e.preventDefault();
    setIsLoading(true)
    // Fetch logged out cart from local storage, transform it and pass it to login body
    const loggedOutCart = getLocalStorageItem("loggedOutCart");
    let loggedOutCartItems: AddItemType[] = [];
    if (loggedOutCart != null) {
      JSON.parse(loggedOutCart).map((item: ILoggedOutShoppingList) => {
        if (item != null && item.product != null && item.product.productID != null) {
          const transformed = {
            productId: item.product.productID,
            quantity: item.quantity
          }
          loggedOutCartItems.push(transformed)
        }
      })
    }
    
    // Include setupOptions
    const loggedOutSetupOptions = getLocalStorageItem("loggedOutSetupOptions")
    const setupOptions: any[] = []
    if (loggedOutSetupOptions != null) {
      JSON.parse(loggedOutSetupOptions).map((entry: any) => setupOptions.push(entry))
    }

    const loggedOutCartItemsString = JSON.stringify([...loggedOutCartItems, ...setupOptions])

    // Fetch logged out wishlist from local storage, transform it and pass it to login body
    const loggedOutWishlist = getLocalStorageItem("loggedOutWishlist")
    const loggedOutWishlistItems: AddItemType[] = [];
    if (loggedOutWishlist != null) {
      JSON.parse(loggedOutWishlist).map((item: ILoggedOutShoppingList) => {
        if (item != null && item.product != null && item.product.productID != null) {
          const transformed = {
            productId: item.product.productID,
            quantity: item.quantity
          }
          loggedOutWishlistItems.push(transformed)
        }
      })
    }
    const loggedOutWishlistItemsString = JSON.stringify(loggedOutWishlistItems)

    await signIn("credentials-login", {
      username,
      password,
      loggedOutCartItemsString,
      loggedOutWishlistItemsString,
      redirect: false
    }).then((res: any) => {
      if (res.ok) {
        removeLocalStorageItem("loggedOutCart");
        removeLocalStorageItem("loggedOutWishlist")
        removeLocalStorageItem("loggedOutSetupOptions")
        setLoggedOutCartCtx(null)
        setLoggedOutWishlistCtx(null)
        if (router.pathname.includes("account/reset-password")) {
          router.push(getNavPath(router, '/'))
        } else if (redirectCallback && redirectCallback == "checkout") {
          window.location.href = getNavPath(router, '/checkout')
        }
      } else {
        setErrorMessage(res.error && res.error!='null' ? res.error : messagesQuery.data.messages["error-login-failed"])
      }
    });

    setIsLoading(false)
  };

  return (
    <LoginContain>
      {messagesQuery.data && <span className="fs-2 fw-bold">
        {messagesQuery.data.messages["login"]}
      </span>}

      {errorMessage && <div className="error-section">
        {errorMessage}
      </div>}

      {messagesQuery.data && <Form className="login-form" onSubmit={handleLoginSubmit}>
        <CustomInput type={InputTypeEnum.EMAIL}
                     label={messagesQuery.data.messages["email-address"]}
                     id={"email"}
                     placeholder={messagesQuery.data.messages["email-address"]}
                     icon={"mail-line"}
                     value={username}
                     setValue={(value: string) => setUsername(value.trim())}/>

        <CustomInput type={InputTypeEnum.PASSWORD}
                     label={messagesQuery.data.messages["password"]}
                     id={"password"}
                     placeholder={messagesQuery.data.messages["password"]}
                     icon={"lock-line"}
                     value={password}
                     setValue={(value: string) => setPassword(value)}/>

        {!isLoading && <Button variant="primary" width="100%" className="mt-1"
                               disabled={username.length < 2 || password.length < 2}>
          {messagesQuery.data.messages["submit"]}
        </Button>}

        {isLoading && <Button variant="primary" width="100%" className="mt-1" disabled={true}>
          <Spinner animation="border" size="sm"/>
        </Button>}
      </Form>}

      <div className="forgot-password">

        <>
          {messagesQuery.data &&
            <Link href={getNavPath(router, 'account/forgot-password')}>
              <span className="text-decoration-underline">{messagesQuery.data.messages["i-forgot-my-password"]}</span>
            </Link>}
        </>
      </div>
    </LoginContain>
  );
}

export default Login;